import { useState, useEffect, useRef } from 'react';
import { customersAPI } from '../../services/api';

// Tarjeta de Cliente + Proyecto + Direccion de instalacion para el detalle
// de cotizacion. Modo lectura por defecto; boton Editar abre el formulario.
// Guarda via PUT /api/quotes/:id (customer_id, project_name,
// install_street/city/state/zip).
export default function CustomerProjectCard({ quote, quoteId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);

  const [customerId, setCustomerId]     = useState(quote.customer_id || null);
  const [customerName, setCustomerName] = useState(quote.customer_name || '');
  const [projectName, setProjectName]   = useState(quote.project_name || '');
  const [street, setStreet]             = useState(quote.install_street || '');
  const [city, setCity]                 = useState(quote.install_city || '');
  const [stateAbbr, setStateAbbr]       = useState(quote.install_state || '');
  const [zip, setZip]                   = useState(quote.install_zip || '');

  const [search, setSearch]     = useState('');
  const [results, setResults]   = useState([]);
  const [showList, setShowList] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    setCustomerId(quote.customer_id || null);
    setCustomerName(quote.customer_name || '');
    setProjectName(quote.project_name || '');
    setStreet(quote.install_street || '');
    setCity(quote.install_city || '');
    setStateAbbr(quote.install_state || '');
    setZip(quote.install_zip || '');
  }, [quote]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (search.trim().length < 3) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await customersAPI.list({ search: search.trim() });
        setResults((data || []).slice(0, 30));
        setShowList(true);
      } catch { setResults([]); }
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [search]);

  const pickCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerName(c.name);
    setSearch('');
    setResults([]);
    setShowList(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`/api/quotes/${quoteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('mq_token')}`,
        },
        body: JSON.stringify({
          customer_id:    customerId,
          project_name:   projectName,
          install_street: street,
          install_city:   city,
          install_state:  stateAbbr,
          install_zip:    zip,
        }),
      });
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      // dejar abierto si fallo
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setCustomerId(quote.customer_id || null);
    setCustomerName(quote.customer_name || '');
    setProjectName(quote.project_name || '');
    setStreet(quote.install_street || '');
    setCity(quote.install_city || '');
    setStateAbbr(quote.install_state || '');
    setZip(quote.install_zip || '');
    setSearch('');
    setResults([]);
    setEditing(false);
  };

  const lbl = { fontSize: 11, color: 'var(--text-3)', marginBottom: 2, fontWeight: 600 };
  const val = { fontSize: 13, color: 'var(--text)' };
  const inp = { width: '100%', fontSize: 12, marginBottom: 8 };

  if (!editing) {
    const addr = [street, city, stateAbbr, zip].filter(Boolean).join(', ');
    return (
      <div style={{ marginBottom: 16 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
          <span className="form-label" style={{ margin: 0 }}>Customer &amp; Project</span>
          <button
            className="btn btn-sm btn-secondary"
            style={{ fontSize: 11, padding: '2px 10px' }}
            onClick={() => setEditing(true)}
          >Edit</button>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{customerName || '— No customer'}</div>
        {quote.tier && (
          <div className="text-muted text-sm">
            {quote.tier} · {quote.has_resale_cert ? 'Tax Exempt' : 'Taxable'}
          </div>
        )}
        {projectName && (
          <div style={{ marginTop: 6 }}>
            <div style={lbl}>Project</div>
            <div style={val}>{projectName}</div>
          </div>
        )}
        {addr && (
          <div style={{ marginTop: 6 }}>
            <div style={lbl}>Installation Address</div>
            <div style={val}>{addr}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <span className="form-label" style={{ margin: 0 }}>Customer &amp; Project</span>
      </div>

      <div style={lbl}>Customer</div>
      {customerName && (
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          {customerName}
          <button
            onClick={() => { setCustomerId(null); setCustomerName(''); }}
            style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}
          >change</button>
        </div>
      )}
      {!customerName && (
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <input
            className="form-input"
            style={inp}
            placeholder="Type 3+ letters to search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => results.length && setShowList(true)}
          />
          {showList && results.length > 0 && (
            <div style={{
              position: 'absolute', zIndex: 20, top: 36, left: 0, right: 0,
              maxHeight: 220, overflowY: 'auto', background: 'var(--bg)',
              border: '1px solid var(--border)', borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            }}>
              {results.map(c => (
                <div
                  key={c.id}
                  onClick={() => pickCustomer(c)}
                  style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #eee' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.city && <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{c.city}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={lbl}>Project Name</div>
      <input
        className="form-input" style={inp}
        placeholder="e.g. Smith Residence – Front Entry"
        value={projectName}
        onChange={e => setProjectName(e.target.value)}
      />

      <div style={lbl}>Installation Address</div>
      <input
        className="form-input" style={inp}
        placeholder="Street"
        value={street}
        onChange={e => setStreet(e.target.value)}
      />
      <div className="flex gap-2">
        <input
          className="form-input" style={{ ...inp, flex: 2 }}
          placeholder="City"
          value={city}
          onChange={e => setCity(e.target.value)}
        />
        <input
          className="form-input" style={{ ...inp, flex: 1 }}
          placeholder="State"
          value={stateAbbr}
          onChange={e => setStateAbbr(e.target.value)}
          maxLength={2}
        />
        <input
          className="form-input" style={{ ...inp, flex: 1 }}
          placeholder="ZIP"
          value={zip}
          onChange={e => setZip(e.target.value)}
        />
      </div>

      <div className="flex gap-2" style={{ marginTop: 8 }}>
        <button className="btn btn-sm btn-primary" style={{ fontSize: 12 }} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-sm btn-secondary" style={{ fontSize: 12 }} onClick={cancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}